import {
  assertSubmittable,
  clockOffsetAt,
  DomainError,
  type MaterialClass,
  parseRequisition,
  REQUISITION_DEPARTMENTS,
  type RequisitionDepartment,
  type ShipTime,
  shortfallLines,
} from '@felix/domain';
import type {
  RequisitionDetail,
  RequisitionFormOptions,
  RequisitionInput,
  RequisitionLineInput,
  RequisitionPriority,
  RequisitionStatus,
  RequisitionSummary,
} from './types.js';

/**
 * Talep ekranlarının saf hesapları (M12b Task 11–12): liste süzgeci, form değerleri ve
 * doğrulaması (gemi ve bulutla aynı şema; `@felix/domain`), onay akışı şeridi ve yorumlar.
 */

export const REQUISITIONS_PATH = '/purchasing/requisitions';

export type RequisitionRoute =
  | { view: 'list' }
  /**
   * `from`: eksik için yeni talebin kaynağı (ADR 0047 §6). `link`: talebi açan arıza ya da HSEQ
   * raporu (`&defect=…`, `&hse=…`, numara `&ref=…`; ADR 0096 §4).
   */
  | {
      view: 'new';
      from?: string;
      link?: RequisitionLink;
      /** Önceden seçili departman (`&department=Provisions`; kumanya talebi, ADR 0099). */
      department?: RequisitionDepartment;
    }
  | { view: 'detail'; id: string }
  | { view: 'edit'; id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Talebin arıza / HSEQ bağı (ADR 0096 §4): yol sözleşmesi arıza ve HSEQ ekranlarıyla ortak. */
export interface RequisitionLink {
  defectId: string | null;
  hseReportId: string | null;
  /** Bağlı kaydın numarası (gösterim ve `defectReportNo` ön dolgusu). */
  ref: string | null;
}

/** Ekranın görünümü sorgu dizesinden (`?new=1`, `?id=…`, `?id=…&edit=1`). */
export function requisitionRoute(search: string): RequisitionRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (id && UUID.test(id)) {
    return params.get('edit') === '1' ? { view: 'edit', id } : { view: 'detail', id };
  }
  if (params.get('new') !== '1') return { view: 'list' };
  const from = params.get('from');
  const uuid = (key: string) => {
    const value = params.get(key);
    return value && UUID.test(value) ? value : null;
  };
  const ref = params.get('ref')?.trim().slice(0, 60) || null;
  const link: RequisitionLink = { defectId: uuid('defect'), hseReportId: uuid('hse'), ref };
  const department = params.get('department');
  const known = (REQUISITION_DEPARTMENTS as readonly string[]).includes(department ?? '');
  return {
    view: 'new',
    ...(from && UUID.test(from) ? { from } : {}),
    ...(link.defectId || link.hseReportId ? { link } : {}),
    ...(known ? { department: department as RequisitionDepartment } : {}),
  };
}

export function requisitionPath(route: RequisitionRoute): string {
  switch (route.view) {
    case 'list':
      return REQUISITIONS_PATH;
    case 'new': {
      if (route.from) return `${REQUISITIONS_PATH}?new=1&from=${route.from}`;
      const params = new URLSearchParams({ new: '1' });
      if (route.link?.defectId) params.set('defect', route.link.defectId);
      if (route.link?.hseReportId) params.set('hse', route.link.hseReportId);
      if (route.link?.ref && (route.link.defectId || route.link.hseReportId)) {
        params.set('ref', route.link.ref);
      }
      if (route.department) params.set('department', route.department);
      return `${REQUISITIONS_PATH}?${params}`;
    }
    case 'detail':
      return `${REQUISITIONS_PATH}?id=${route.id}`;
    case 'edit':
      return `${REQUISITIONS_PATH}?id=${route.id}&edit=1`;
  }
}

export interface RequisitionFilter {
  vesselId: string | null;
  status: RequisitionStatus | null;
  /** Talep günü aralığı (`YYYY-MM-DD`, ikisi de dahil). */
  from: string | null;
  to: string | null;
  /** Ofiste inceleme (ADR 0111 §A): `needed` onaylı ve revizyonsuz, `revised` revizyonlu. */
  review?: 'needed' | 'revised' | null;
  /** Talep türü (ADR 0122 §6): bölümden türer; `Provisions` kumanya talebidir. */
  kind?: MaterialClass | null;
}

/** Kumanya talebinin bölümü (ADR 0047 §4; ADR 0122 §6). */
export const PROVISION_DEPARTMENT = 'Provisions';

/** Talebin türü: `Provisions` bölümü kumanya, öteki bölümler stores & spares. */
export function requisitionKindOf(department: string): MaterialClass {
  return department === PROVISION_DEPARTMENT ? 'provision' : 'stores';
}

/**
 * Talepte malzeme seçicisinin havuzu (ADR 0122 §6): bölüm `Provisions` ise kumanya, öteki
 * bölümlerde stores & spares; bölüm seçilmemişse süzgeç yok. Serbest kalem yine yazılabilir.
 */
export function pickerClassOf(department: string | null | undefined): MaterialClass | undefined {
  return department ? requisitionKindOf(department) : undefined;
}

export const NO_FILTER: RequisitionFilter = {
  vesselId: null,
  status: null,
  from: null,
  to: null,
  review: null,
  kind: null,
};

/**
 * Departmanın ekrandaki adı (M12c-R19): talep ekranları, PO kartı ve Requisition Summary aynı
 * anahtarla çevirir; bilinmeyen değer olduğu gibi.
 */
export function departmentLabel(
  value: string | null,
  t: (key: string, options: { defaultValue: string }) => string,
): string {
  if (!value) return '—';
  return t(`purchasing.departments.${value}`, { defaultValue: value });
}

/** Listede ve raporda görünen bütün talep durumları (ADR 0040 #188: "PO Issued", "Closed"). */
const REQUISITION_STATUS_OPTIONS: readonly RequisitionStatus[] = [
  'draft',
  'pending_approval',
  'office_approval',
  'approved',
  'in_progress',
  'po_issued',
  'closed',
  'returned',
  'cancelled',
];

/**
 * Durum süzgecinin seçenekleri (M12c-R16, M12c-R29): talep listesi ve Requisition Summary aynı
 * listeyi kullanır. "In Progress" ofiste RFQ'dan türer; gemide oluşmaz, gösterilmez.
 */
export function requisitionStatusOptions(scope: 'ship' | 'office'): RequisitionStatus[] {
  return REQUISITION_STATUS_OPTIONS.filter(
    (status) => scope === 'office' || status !== 'in_progress',
  );
}

export function filterRequisitions(
  items: readonly RequisitionSummary[],
  filter: RequisitionFilter,
): RequisitionSummary[] {
  return items.filter(
    (item) =>
      (!filter.vesselId || item.vesselId === filter.vesselId) &&
      (!filter.status || item.status === filter.status) &&
      (!filter.from || item.requestDate >= filter.from) &&
      (!filter.to || item.requestDate <= filter.to) &&
      (!filter.review || item.review === filter.review) &&
      (!filter.kind || requisitionKindOf(item.department) === filter.kind),
  );
}

// --- Form ---

/** Sayı girişinin form değeri: sayı, yazım sırasındaki ara metin ya da boş (`''`). */
export type NumberValue = number | string;

export interface FormLine {
  /** Satırın ekrandaki kimliği (React anahtarı). */
  key: string;
  materialId: string | null;
  name: string;
  description: string;
  /**
   * Sayı girişinin değeri olduğu gibi (Mantine `NumberInput`): yazılırken "85." gibi ara değer
   * metindir. Sayıya yalnız toplamda, doğrulamada ve girdide `numberOf` ile çevrilir.
   */
  quantity: NumberValue;
  unit: string;
  unitPrice: NumberValue;
  priority: RequisitionPriority;
  /**
   * Gemide kalemin stok mevcudu (ADR 0111 §A): "In stock: 5" rozeti; içerik değildir, girdiye
   * girmez (gemi sunucusu kayıtta kendisi yazar).
   */
  onHand?: number | null;
}

export interface RequisitionFormValues {
  department: string;
  requestedBy: string;
  requiredDate: string;
  port: string;
  /** Liman listesindeki kod (ADR 0048 §3); serbest metinde null. */
  portCode: string | null;
  reason: string;
  currency: string;
  remarks: string;
  workOrderNo: string;
  defectReportNo: string;
  /** Arıza ve HSEQ raporu bağı (ADR 0096 §4); formda alan değil, korunur. */
  defectId: string | null;
  hseReportId: string | null;
  lines: FormLine[];
}

let counter = 0;
export function lineKey(): string {
  counter += 1;
  return `line-${counter}`;
}

export function emptyForm(options: RequisitionFormOptions): RequisitionFormValues {
  return {
    department: '',
    requestedBy: options.requesters.some((r) => r.id === options.me) ? options.me : '',
    requiredDate: '',
    port: '',
    portCode: null,
    reason: '',
    currency: options.baseCurrency,
    remarks: '',
    workOrderNo: '',
    defectReportNo: '',
    defectId: null,
    hseReportId: null,
    lines: [],
  };
}

/** Arızadan ya da HSEQ raporundan yeni talep (ADR 0096 §4): bağ ve numara ön dolgusu. */
export function linkedForm(
  form: RequisitionFormValues,
  link: RequisitionLink,
): RequisitionFormValues {
  return {
    ...form,
    defectId: link.defectId,
    hseReportId: link.hseReportId,
    defectReportNo: link.ref ?? form.defectReportNo,
    reason: form.reason || (link.defectId ? 'Spare Parts' : form.reason),
  };
}

export function formFromDetail(detail: RequisitionDetail): RequisitionFormValues {
  return {
    department: detail.department,
    requestedBy: detail.requestedBy.id,
    requiredDate: detail.requiredDate,
    port: detail.port ?? '',
    portCode: detail.portCode ?? null,
    reason: detail.reason ?? '',
    currency: detail.currency,
    remarks: detail.remarks ?? '',
    workOrderNo: detail.workOrderNo ?? '',
    defectReportNo: detail.defectReportNo ?? '',
    defectId: detail.defectId ?? null,
    hseReportId: detail.hseReportId ?? null,
    lines: detail.lines.map((line) => ({
      key: lineKey(),
      materialId: line.materialId,
      name: line.name,
      description: line.description ?? '',
      quantity: line.quantity,
      unit: line.unit,
      unitPrice: line.unitPrice ?? '',
      priority: line.priority,
      ...(line.onHand === undefined ? {} : { onHand: line.onHand }),
    })),
  };
}

/**
 * Eksik için yeni talebin formu (ADR 0047 §6): başlık kaynak talepten (istenen tarih boş),
 * açıklamada kaynak numara; satırlar kalan miktarla (`shortfallLines`). Kullanıcı düzeltip
 * kaydeder; talep normal onaydan geçer.
 */
export function shortfallForm(detail: RequisitionDetail, sourceNo: string): RequisitionFormValues {
  const lines = detail.fulfilment ? shortfallLines(detail.fulfilment, detail.currency) : [];
  return {
    department: detail.department,
    requestedBy: detail.requestedBy.id,
    requiredDate: '',
    port: detail.port ?? '',
    portCode: detail.portCode ?? null,
    reason: detail.reason ?? '',
    currency: detail.currency,
    remarks: `Shortfall of ${sourceNo}`,
    workOrderNo: detail.workOrderNo ?? '',
    defectReportNo: detail.defectReportNo ?? '',
    defectId: detail.defectId ?? null,
    hseReportId: detail.hseReportId ?? null,
    lines: lines.map((line) => ({
      key: lineKey(),
      materialId: line.materialId,
      name: line.name,
      description: '',
      quantity: line.quantity,
      unit: line.unit,
      unitPrice: line.unitPrice ?? '',
      priority: 'Normal',
    })),
  };
}

const text = (value: string) => (value.trim() === '' ? null : value.trim());
const orNull = (value: number | '') => (value === '' ? null : value);
const orUndefined = (value: number | '') =>
  value === '' ? (undefined as unknown as number) : value;

export function toInput(values: RequisitionFormValues): RequisitionInput {
  return {
    department: values.department,
    requestedBy: values.requestedBy,
    requiredDate: values.requiredDate,
    port: text(values.port),
    portCode: text(values.port) === null ? null : values.portCode,
    reason: text(values.reason),
    currency: values.currency,
    remarks: text(values.remarks),
    workOrderNo: text(values.workOrderNo),
    defectReportNo: text(values.defectReportNo),
    defectId: values.defectId,
    hseReportId: values.hseReportId,
    lines: values.lines.map(
      (line): RequisitionLineInput => ({
        materialId: line.materialId,
        name: line.name,
        description: text(line.description),
        // Boş miktar şemada "REQUIRED" olur
        quantity: orUndefined(numberOf(line.quantity)),
        unit: line.unit,
        unitPrice: orNull(numberOf(line.unitPrice)),
        priority: line.priority,
      }),
    ),
  };
}

/** Satır tutarı: miktar × birim fiyat; fiyat ya da miktar yoksa `null`. */
export function lineTotal(line: Pick<FormLine, 'quantity' | 'unitPrice'>): number | null {
  const quantity = numberOf(line.quantity);
  const unitPrice = numberOf(line.unitPrice);
  if (quantity === '' || unitPrice === '') return null;
  return Math.round(quantity * unitPrice * 1e6) / 1e6;
}

/** Formun toplamı; fiyatsız satır varsa `value` boştur (sorular #71). */
export function formTotal(lines: readonly FormLine[]): { value: number | null; priced: number } {
  let priced = 0;
  let unknown = false;
  for (const line of lines) {
    const total = lineTotal(line);
    if (total === null) unknown = true;
    else priced += total;
  }
  const rounded = Math.round(priced * 1e6) / 1e6;
  return { value: unknown ? null : rounded, priced: rounded };
}

/**
 * Formun hataları, alan yoluna göre (`department`, `lines.0.quantity`, `lines`): gemi ve bulut
 * aynı şemayla doğrular. Gönderimde satır ve istenen tarih de denetlenir.
 */
export function validateForm(
  values: RequisitionFormValues,
  options: { submit: boolean; today: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const parsed = parseRequisition(toInput(values));
  if (!parsed.ok) {
    for (const issue of parsed.issues) errors[issue.path] ??= issue.code;
    return errors;
  }
  if (options.submit) {
    try {
      assertSubmittable(parsed.value, options.today);
    } catch (error) {
      const code = error instanceof DomainError ? error.code : 'GENERIC';
      errors[code === 'NO_LINES' ? 'lines' : 'requiredDate'] = code;
    }
  }
  return errors;
}

// --- Onay akışı (`satin-alma-akisi.jpeg` "Approval Workflow") ---

export type WorkflowNodeState = 'done' | 'returned' | 'current' | 'waiting';

export interface WorkflowNode {
  key: string;
  /**
   * Çeviri anahtarı: `requested`, `approved`, `returned`, `pending`, `officePending`, `waiting`,
   * `escalated` (ofis adımına katılan gemi adımı; ADR 0091), `poIssued`, `draft`.
   */
  label: string;
  role: string | null;
  at: string | null;
  state: WorkflowNodeState;
  office: boolean;
}

/**
 * Şerit: talep → kuralın adımları → sipariş (M12c). Son gönderimin adımları; iade edilen talepte
 * iade eden adım kırmızı, taslakta yalnız "Requested" bekler.
 */
export function workflowNodes(detail: RequisitionDetail): WorkflowNode[] {
  const submitted = [...detail.timeline].reverse().find((e) => e.kind === 'submitted');
  const nodes: WorkflowNode[] = [
    {
      key: 'requested',
      label: submitted ? 'requested' : 'draft',
      role: submitted?.role ?? detail.requestedBy.role,
      at: submitted?.at ?? null,
      state: submitted ? 'done' : 'current',
      office: false,
    },
  ];
  for (const step of detail.steps) {
    nodes.push({
      key: `step-${step.step}`,
      label:
        step.status === 'approved'
          ? 'approved'
          : step.status === 'returned'
            ? 'returned'
            : step.status === 'pending'
              ? step.office
                ? 'officePending'
                : 'pending'
              : step.status === 'escalated'
                ? 'escalated'
                : 'waiting',
      role: step.roleName,
      at: step.at,
      state:
        step.status === 'approved'
          ? 'done'
          : step.status === 'returned'
            ? 'returned'
            : step.status === 'pending'
              ? 'current'
              : 'waiting',
      office: step.office,
    });
  }
  // Sipariş (ADR 0040 #188): onaylı PO tamamlanmış düğüm; taslak ya da onay bekleyen PO şu anki;
  // iptal edilen PO yok sayılır
  const order = detail.order && detail.order.status !== 'cancelled' ? detail.order : null;
  const issued = order !== null && order.status !== 'draft' && order.status !== 'pending_approval';
  nodes.push({
    key: 'po',
    label: 'poIssued',
    role: order?.poNo ?? null,
    at: null,
    state: issued ? 'done' : order ? 'current' : 'waiting',
    office: true,
  });
  return nodes;
}

/** Onay durumu notu: çeviri anahtarı (`purchasing.workflow.notes.<key>`) ve değerleri. */
export interface ApprovalNote {
  key:
    | 'escalated'
    | 'ownEscalated'
    | 'awaitingShip'
    | 'awaitingOffice'
    | 'decideHere'
    | 'decideByRole'
    | 'decidedOnBoard'
    | 'ownWaiting'
    | 'officeWaiting';
  params: Record<string, string>;
}

/**
 * Bekleyen talebin onay durumu (ADR 0091): kimin onayı bekleniyor (gemide ya da ofiste), kararın
 * nerede verileceği ve kayıt kendi kaydını onaylayamayan bir onaylayıcıdan geldiyse adımın neden
 * ofise gittiği. Gemide gönderen kişi ("You submitted …") ayrıca söylenir.
 */
export function approvalNotes(detail: RequisitionDetail, scope: 'ship' | 'office'): ApprovalNote[] {
  const pending = detail.steps.find((s) => s.status === 'pending');
  if (!pending || detail.cancelledAt) return [];
  const notes: ApprovalNote[] = [];
  const escalated = detail.steps.find((s) => s.escalation);
  if (escalated?.escalation) {
    // Katılan adımın onayı kuralın ofis adımındadır
    const officeStep =
      escalated.escalation.to === 'office'
        ? escalated
        : (detail.steps.find((s) => s.office && !s.escalation) ?? pending);
    const params = { role: officeStep.roleName, shipRole: escalated.escalation.shipRoleName };
    notes.push({
      key: scope === 'ship' && detail.can.ownRecord ? 'ownEscalated' : 'escalated',
      params,
    });
  }
  const role = { role: pending.roleName };
  if (pending.office) {
    notes.push({ key: 'awaitingOffice', params: role });
    if (scope === 'office') {
      notes.push(
        detail.can.decide
          ? { key: 'decideHere', params: {} }
          : { key: 'decideByRole', params: role },
      );
    } else {
      notes.push({ key: 'officeWaiting', params: {} });
    }
    return notes;
  }
  notes.push({ key: 'awaitingShip', params: role });
  if (scope === 'office') notes.push({ key: 'decidedOnBoard', params: role });
  else if (detail.can.decide) notes.push({ key: 'decideHere', params: {} });
  else if (detail.can.ownRecord) notes.push({ key: 'ownWaiting', params: role });
  return notes;
}

/** "Approval Comments": kararlar ve gerekçeleri (iade gerekçesi dahil), eskiden yeniye. */
export function approvalComments(detail: RequisitionDetail) {
  return detail.timeline.filter(
    (event) => event.kind === 'approved' || event.kind === 'returned' || event.kind === 'cancelled',
  );
}

/**
 * Yerel saatle `GG.AA.YYYY SS:DD` (tasarımdaki biçim); gemi saati verilmezse tarayıcınınki. Saat
 * çizelgesi verilirse gemi saati kaydından (ADR 0065 §4).
 */
export function formatStampLocal(at: string | null, timeZone?: ShipTime): string {
  if (!at) return '—';
  const date = new Date(at);
  if (timeZone !== undefined && typeof timeZone !== 'string') {
    const local = new Date(date.getTime() + clockOffsetAt(timeZone, date.getTime()) * 60_000);
    const iso = local.toISOString();
    return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)} ${iso.slice(11, 16)}`;
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get('minute')}`;
}

/** Tutar biçimi: iki ondalık, dile göre ayraç (tasarımda `2,850.00`). */
export function formatAmount(value: number | null, locale: string): string {
  if (value === null) return '—';
  return new Intl.NumberFormat(locale === 'tr' ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Miktar biçimi: en çok üç ondalık, dile göre ayraç (CLAUDE.md "sayı biçimleri dile göre"). */
export function formatQuantity(value: number, locale: string): string {
  return new Intl.NumberFormat(locale === 'tr' ? 'tr-TR' : 'en-US', {
    maximumFractionDigits: 3,
  }).format(value);
}

/** Sayı girişinin ayraçları, dile göre (tutarların biçimiyle aynı). */
export function numberSeparators(locale: string): {
  thousandSeparator: string;
  decimalSeparator: string;
} {
  return locale === 'tr'
    ? { thousandSeparator: '.', decimalSeparator: ',' }
    : { thousandSeparator: ',', decimalSeparator: '.' };
}

/**
 * Sayı girişinin sayısı. Mantine `NumberInput` yazılırken ara değeri ("1.", "0.", "1.40") metin
 * olarak verir; değer biçimsizdir (binlik ayracı yok, ondalık ayracı nokta). Boş ya da geçersiz
 * giriş `''`. Form değeri metin kalır, bu fonksiyon yalnız toplamda, doğrulamada ve girdide
 * çağrılır: ara değer forma sayı olarak yazılırsa alan "1."den "1"e döner ve son ondalık hane
 * silinince sonraki rakam tam kısma eklenir (1.5 → 1. → 17).
 */
export function numberOf(value: number | string): number | '' {
  if (typeof value === 'number') return Number.isFinite(value) ? value : '';
  const parsed = Number(value.replace(/[^0-9.-]/g, ''));
  return value.trim() === '' || !Number.isFinite(parsed) ? '' : parsed;
}

/** Form ilk haliyle aynı mı (satırların ekran anahtarları dışında). */
export function formIsDirty(
  initial: RequisitionFormValues,
  current: RequisitionFormValues,
): boolean {
  const plain = (values: RequisitionFormValues) =>
    JSON.stringify({
      ...values,
      lines: values.lines.map(({ key: _key, onHand: _onHand, ...line }) => line),
    });
  return plain(initial) !== plain(current);
}

export type SortDirection = 'asc' | 'desc';

/**
 * Tablo sıralaması (CLAUDE.md "tablolar sıralanabilir"): sayı sayı gibi, metin doğal sırayla; boş
 * değer her yönde sondadır; eşit değerlerde giriş sırası korunur (kararlı sıralama).
 */
export function sortItems<T>(
  items: readonly T[],
  direction: SortDirection,
  value: (item: T) => string | number | null,
): T[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
    if (typeof left === 'number' && typeof right === 'number') return sign * (left - right);
    return sign * String(left).localeCompare(String(right), 'en', { numeric: true });
  });
}
